import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { ApiResponse } from "types/index";
import {
  createApiClient,
  ExtendedAxiosRequestConfig,
  SharedApiClient,
} from "./apiClientShared";

/**
 * Authenticated API client. Adds Bearer tokens on requests and preserves the
 * historical post(url, data, params?, config?) signature plus resendCode / downloadBlob.
 */
class ApiService {
  private client: SharedApiClient;

  constructor() {
    this.client = createApiClient({ withAuth: true });
  }

  async get<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    return this.client.get<T>(url, config);
  }

  async post<T = any>(
    url: string,
    data?: any,
    params?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    return this.client.post<T>(url, data, { ...config, params });
  }

  async resendCode<T = any>(
    url: string,
    params?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> =
      await this.client.getInstance().post(url, null, {
        ...config,
        params,
      });
    return response.data;
  }

  async put<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    return this.client.put<T>(url, data, config);
  }

  async patch<T = any>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    return this.client.patch<T>(url, data, config);
  }

  async delete<T = any>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    return this.client.delete<T>(url, config);
  }

  async uploadFile<T = any>(
    url: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ApiResponse<T>> {
    return this.client.uploadFile<T>(url, file, onUploadProgress);
  }

  async downloadBlob(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<Blob> {
    const response = await this.client.getInstance().get(url, {
      ...config,
      responseType: "blob",
      metadata: { skipGlobalLoading: true },
    } as ExtendedAxiosRequestConfig);
    return response.data as Blob;
  }

  getInstance(): AxiosInstance {
    return this.client.getInstance();
  }
}

export const apiService = new ApiService();
export default apiService;
